import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { useNetworkMetadata } from "Flocknote/apiHooks";
import { useIsLoggedIn } from "Flocknote/stateHooks";

import FnrLoadingBricks from "UI/FnrLoadingBricks2";
import FnrSimpleError from "UI/FnrSimpleError";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";

import { useDebounce } from "Utilities/Hooks/useDebounce";
import { LoadMore } from "Utilities/Hooks/useIntersectionObserver";
import useIsWiderThan from "Utilities/Hooks/useIsWiderThan";

import { useGetFontData, useNotes } from "../../API/queryHooks";
import { getFontFamily } from "../../constants/fonts";

import NotePopover from "./NotePopover";
import { NotesHeader, NotesList, PageLoadingOverlay } from "./components";
import { useNotePopover } from "./hooks";

const NotesPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { notes, isLoading, error, loadMoreRef, isFetchingNextPage } =
    useNotes(debouncedSearchTerm);
  const isLoggedIn = useIsLoggedIn();

  const [networkMetadata] = useNetworkMetadata();
  const { networkType } = networkMetadata;
  const NotloggedInAndClosedNetwork = !isLoggedIn && networkType === "closed";
  const NotloggedInAndNoNotes = !isLoggedIn && notes.length === 0;

  const { selectedFont } = useGetFontData();
  const currentFontFamily = getFontFamily(selectedFont);

  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const containerRef = useRef();
  const isMobile = !useIsWiderThan(containerRef, { px: 650 }) || false;

  // Use custom hook for popover logic
  const {
    popoverOpen,
    selectedNoteId,
    handleNoteClick,
    handleClosePopover,
    triggerLogin,
  } = useNotePopover(isLoading, hasInitiallyLoaded);

  useEffect(
    function setPageTitle() {
      if (networkMetadata?.networkName)
        document.title = `${networkMetadata.networkName} // Notes`;
    },
    [networkMetadata?.networkName]
  );

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Track initial load for different loading states
  useEffect(() => {
    if (!isLoading && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
    }
  }, [isLoading, hasInitiallyLoaded]);

  // Determine if we should show full page loader vs inline loader
  const isInitialLoad = isLoading && !hasInitiallyLoaded;
  const isSubsequentLoad = isLoading && hasInitiallyLoaded;

  return (
    <PageWrapper ref={containerRef}>
      <PageLoadingOverlay isInitialLoad={isInitialLoad} />

      <ContentContainer $isLoading={isInitialLoad}>
        <NotesHeader
          isLoggedIn={isLoggedIn}
          isMobile={isMobile}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          error={error}
          hasNotes={notes.length > 0}
        />

        <NotesContainer $isMobile={isMobile}>
          <ErrorMessage show={error} helpLink>
            Oh no! There was an issue loading your notes.
          </ErrorMessage>

          <NotesList
            notes={notes}
            isMobile={isMobile}
            currentFontFamily={currentFontFamily}
            handleNoteClick={handleNoteClick}
            hasInitiallyLoaded={hasInitiallyLoaded}
            isSubsequentLoad={isSubsequentLoad}
            searchTerm={debouncedSearchTerm}
            triggerLogin={triggerLogin}
            NotloggedInAndClosedNetwork={NotloggedInAndClosedNetwork}
            NotloggedInAndNoNotes={NotloggedInAndNoNotes}
          />
        </NotesContainer>

        {isFetchingNextPage && !error ? (
          <FnrHorizontalGap
            fullWidth
            justifyContent="center"
            style={{ padding: 60 }}
          >
            <FnrLoadingBricks color="white" size={32} loading={true} />
          </FnrHorizontalGap>
        ) : (
          <LoadMore ref={loadMoreRef} />
        )}
      </ContentContainer>

      <NotePopover
        isOpen={popoverOpen}
        onClose={handleClosePopover}
        noteId={selectedNoteId}
      />
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  width: 95vw;
  max-width: 1100px;
  padding: 0 0 220px 0;
  font-family: "Open Sans", sans-serif;
  margin: 0 auto;

  @media (max-width: 800px) {
    padding: 10px;
  }

  @media (max-width: 600px) {
    width: 100vw;
    padding: 10px 0;
  }
`;

const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${({ $isMobile }) => ($isMobile ? "12px" : "0")};
`;

const ContentContainer = styled.div`
  opacity: ${({ $isLoading }) => ($isLoading ? 0 : 1)};
  transform: ${({ $isLoading }) =>
    $isLoading ? "translateY(20px)" : "translateY(0)"};
  transition: opacity 0.4s ease, transform 0.4s ease;
`;

const ErrorMessage = styled(FnrSimpleError)`
  text-align: center;
  margin: auto;
  max-width: 400px;
`;

export default NotesPage;
